// The live tab icon (brand.md §10, R1653; mockup round 079, card 26).
//
// A page redraws its own site icon while it runs. It builds the sign with
// `signSVGElement` of `ui/sign.ts` and writes it as a data URL into the
// `href` of the SVG `<link rel="icon">` of the page head. The two PNG
// links stay as they are: a browser that takes the SVG link shows the live
// icon, and a browser that takes a PNG keeps the static one.
//
// The icon has four states:
//
// - **The faction accent.** The edge of the sign takes the accent the
//   chrome wears (`--accent` on the document root, which `ui/theme.ts`
//   writes; AUTO follows the faction), and the glyph takes its bright.
// - **A dot**, while the tab is hidden and a siege warning or an unread
//   INBOX item waits: a disc in the frozen bad ink for the siege warning,
//   in the frozen gold for the INBOX. The siege warning wins. A siege
//   warning is a notice of the push class `siege` that is not calm: a
//   pledge of another side at an object of the faction, or a rally of a
//   squad mate. The event ids of the class come off `BUZZ_EVENTS`, the one
//   list the BUZZ and the PUSH column of the ALERTS card hear, less the
//   calm events of `BUZZ_CALM_EVENTS` (an attack pledge of the own faction
//   and the windows of the day, R3085), so no event id is a literal here.
// - **A pulse**, while the tab is hidden after the arcade queue found a
//   match: the sign and a flash (the sign in the page ink on a bright
//   plate) take turns once a second. A hidden tab runs a timer once a
//   second at most, so the pulse steps on a 1 s timer and never on an
//   animation frame.
// - **The plain state** comes back when the tab becomes visible: the siege
//   warning is read, the pulse stops, and the INBOX count draws no dot.
//
// A page of the dev identity draws the dev corner in every state, the
// flash included (brand.md §15, R2880).
//
// The page builds ONE icon (`installTabIcon`); a module that knows a state
// reaches it through `pageTabIcon`. The game page, the hub on the same
// page and the arcade page install it. A page with no SVG icon link
// installs nothing, and every call is then a no-op.

import { PUSH_SIEGE } from '../gen/balance';
import {
  ACCENT_BRIGHT_CSS, ACCENT_CSS, BUZZ_CALM_EVENTS, BUZZ_EVENTS, PALETTE_TOKENS, SIGN,
} from '../gen/presentation';
import { LIGHT_CHROME_FROZEN } from '../gen/inks';
import { devBrand, signSVGElement } from './sign';
import { hexOf, over, rgbOfHex } from './theme';

/** The dot of a hidden tab: a siege warning, an unread INBOX item, none. */
export type TabDot = 'siege' | 'inbox' | null;

/** What the icon shows at one moment. */
export interface TabIconView {
  /** The accent of the sign's edge, `#rrggbb`. */
  accent: string;
  dot: TabDot;
  /** The frame of the pulse: 0 the sign, 1 the flash; -1 is no pulse. */
  pulse: -1 | 0 | 1;
  /** True on a page of the dev identity: the dev corner stands in every
   *  state (brand.md §15). Absent is prod. */
  dev?: boolean;
}

// The proportions of card 26 in the 24-unit box of the sign. They are the
// picture of the card and not figures a designer tunes, so they stay here
// (the rule of `render/effect-recipe-kit.ts`).

/** The dot stands on the top-right corner of the box. */
const DOT_X = 18.6;
const DOT_Y = 5.4;
/** The ring of page ink that parts the dot from the sign. */
const DOT_RING_R = 6.4;
/** The disc of the dot. */
const DOT_R = 4.6;
/** The corner of the flash plate. */
const FLASH_RX = 5.5;
/** How far the bright of a faction accent steps toward white. Faction 1
 *  takes `[presentation.accent] bright` itself (card 26, `brightOf`). */
const BRIGHT_STEP = 0.82;
/** The size the icon is drawn for: a tab draws its icon at 16 CSS px. */
const ICON_PX = 16;
/** The step of the pulse, in ms. A hidden tab runs a timer once a second
 *  at most, so a shorter step would not run any faster. */
export const PULSE_MS = 1000;

/** The page ink: the ring of the dot and the sign of the flash. It is
 *  `[presentation.palette] ink`, which `SIGN` carries as its ground. */
const INK = SIGN.ground;

/** The dark frozen ink of one token. The tab icon stands outside the page
 *  on its own dark ground, so it keeps the dark identity (R3298): its dot
 *  takes the dark bad and gold inks, as its edge takes the accent of the
 *  page root, which stays dark. */
function darkFrozen(token: string): string {
  return LIGHT_CHROME_FROZEN.find((f) => f.token === token)?.dark ?? INK;
}
const DOT_BAD = darkFrozen('bad');
const DOT_GOLD = darkFrozen('gold');

/** The event ids of the push class `siege` that are not calm: the siege
 *  warnings (R3085). */
export const SIEGE_WARNINGS: ReadonlySet<string> = new Set(
  (BUZZ_EVENTS.find((r) => r.push === PUSH_SIEGE)?.events ?? [])
    .filter((id) => !BUZZ_CALM_EVENTS.includes(id)),
);

/** The bright of an accent: the ink of the glyph. The accent of the sign
 *  takes its own bright, and every other accent the same step toward
 *  white (brand.md §1.5). */
export function signTint(accent: string): string {
  const a = accent.trim().toLowerCase();
  if (a === ACCENT_CSS) return ACCENT_BRIGHT_CSS;
  return hexOf(over([255, 255, 255], rgbOfHex(a), BRIGHT_STEP));
}

/** The markup of the icon for one view. Pure, so a test reads it. */
export function tabIconSvg(v: TabIconView): string {
  const bright = signTint(v.accent);
  const flash = v.pulse === 1;
  const corner = v.dev === true;
  const svg = flash
    ? signSVGElement(ICON_PX, { cut: 'small', tone: 'mono', ink: INK, corner })
    : signSVGElement(ICON_PX, { cut: 'small', shape: 'round', accent: v.accent, bright, id: 'tab', corner });
  const under = flash ? `<rect width="24" height="24" rx="${FLASH_RX}" fill="${bright}"/>` : '';
  const dotInk = v.dot === 'siege' ? DOT_BAD : v.dot === 'inbox' ? DOT_GOLD : '';
  const dot = dotInk === '' ? ''
    : `<circle cx="${DOT_X}" cy="${DOT_Y}" r="${DOT_RING_R}" fill="${INK}"/>`
      + `<circle cx="${DOT_X}" cy="${DOT_Y}" r="${DOT_R}" fill="${dotInk}"/>`;
  // The flash plate goes under the sign and the dot over it: after the
  // opening tag and before the closing one.
  const open = svg.indexOf('>') + 1;
  const close = svg.lastIndexOf('</svg>');
  return svg.slice(0, open) + under + svg.slice(open, close) + dot + svg.slice(close);
}

/** The icon as the `href` of a `<link>`. */
export function tabIconHref(v: TabIconView): string {
  return `data:image/svg+xml,${encodeURIComponent(tabIconSvg(v))}`;
}

/** The accent the chrome wears: the `--accent` that `ui/theme.ts` writes
 *  on the document root, else the first paint of the token sheet. */
export function chromeAccent(): string {
  if (typeof document === 'undefined') return PALETTE_TOKENS['--accent'];
  const v = document.documentElement.style.getPropertyValue('--accent').trim();
  return /^#[0-9a-f]{6}$/i.test(v) ? v.toLowerCase() : PALETTE_TOKENS['--accent'];
}

/** The SVG icon link of the page head, or null. */
function svgIconLink(): HTMLLinkElement | null {
  if (typeof document === 'undefined') return null;
  return document.querySelector<HTMLLinkElement>('link[rel~="icon"][type="image/svg+xml"]')
    ?? document.querySelector<HTMLLinkElement>('link[rel~="icon"][href$=".svg"]');
}

export class TabIcon {
  private inbox = 0;
  private siege = false;
  /** True from a found match until the tab is visible again. */
  private pulsing = false;
  private frame: 0 | 1 = 0;
  private timer = 0;
  private written = '';
  private readonly original: string | null;
  private readonly observer: MutationObserver | null;

  constructor(private readonly link: HTMLLinkElement) {
    this.original = link.getAttribute('href');
    document.addEventListener('visibilitychange', this.onVisibility);
    // `ui/theme.ts` writes the accent on the root's inline style, and the
    // root's `data-theme` with it. The icon follows every write; a write
    // that leaves the accent as it stood draws nothing.
    this.observer = typeof MutationObserver === 'function'
      ? new MutationObserver(() => this.paint()) : null;
    this.observer?.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'data-theme'] });
    this.paint();
  }

  /** What the icon shows now. */
  view(): TabIconView {
    const hidden = document.hidden === true;
    let dot: TabDot = null;
    if (hidden) dot = this.siege ? 'siege' : this.inbox > 0 ? 'inbox' : null;
    return { accent: chromeAccent(), dot, pulse: hidden && this.pulsing ? this.frame : -1, dev: devBrand() };
  }

  /** The unread INBOX count: the dock's INBOX badge, or the seats of the
   *  hub's account. */
  setInbox(n: number): void {
    const v = Math.max(0, Math.floor(n) || 0);
    if (v === this.inbox) return;
    this.inbox = v;
    this.paint();
  }

  /** A notice of the world: the siege warning marks a hidden tab. */
  onNotice(eventId: string): void {
    if (!SIEGE_WARNINGS.has(eventId) || document.hidden !== true) return;
    this.siege = true;
    this.paint();
  }

  /** The arcade queue found a match: a hidden tab pulses until it is seen. */
  matchFound(): void {
    if (document.hidden !== true || this.pulsing) return;
    this.pulsing = true;
    this.frame = 0;
    this.timer = window.setInterval(this.step, PULSE_MS);
    this.paint();
  }

  /** The match went away before the tab was seen: the pulse stops. */
  clearPulse(): void {
    if (!this.pulsing) return;
    this.stopPulse();
    this.paint();
  }

  /** Takes the listeners off and gives the link its static icon back. */
  dispose(): void {
    this.stopPulse();
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.original !== null) this.link.setAttribute('href', this.original);
    this.written = '';
    if (installed === this) installed = null;
  }

  /** Writes the icon when it changed. */
  paint(): void {
    const href = tabIconHref(this.view());
    if (href === this.written) return;
    this.written = href;
    this.link.setAttribute('href', href);
  }

  private readonly step = (): void => {
    this.frame = this.frame === 0 ? 1 : 0;
    this.paint();
  };

  private stopPulse(): void {
    this.pulsing = false;
    this.frame = 0;
    if (this.timer !== 0) window.clearInterval(this.timer);
    this.timer = 0;
  }

  private readonly onVisibility = (): void => {
    if (document.hidden !== true) {
      // The player looks at the page: what waited has been seen.
      this.siege = false;
      this.stopPulse();
    }
    this.paint();
  };
}

/** The icon of this page, once `installTabIcon` ran. */
let installed: TabIcon | null = null;

/** Builds the icon of this page on its SVG icon link. A page with no such
 *  link, or a second call, gets the icon that stands (or null). */
export function installTabIcon(): TabIcon | null {
  if (installed) return installed;
  const link = svgIconLink();
  if (!link) return null;
  installed = new TabIcon(link);
  return installed;
}

/** The icon of this page, or null where none is installed. */
export function pageTabIcon(): TabIcon | null {
  return installed;
}
